
import { useState } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { 
  sendAccountSetupInvitation, 
  sendBulkAccountSetupInvitations,
  InvitationResponse
} from '@/services/admin/invitationService';

/**
 * Hook for managing client invitations
 */
export const useClientInvitations = (
  onboardingRequests: any[],
  setOnboardingRequests: React.Dispatch<React.SetStateAction<any[]>>
) => {
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const handleSendInvitation = async (client: any) => {
    console.log('Sending invitation to client:', client);
    setIsProcessing(true);
    
    try {
      const result: InvitationResponse = await sendAccountSetupInvitation(client.id);
      
      if (result.success && result.results[0]?.success) {
        // Update local state to reflect invitation sent
        setOnboardingRequests((prevRequests) => 
          prevRequests.map(req => 
            req.id === client.id 
              ? { 
                  ...req, 
                  invitation_sent_at: new Date().toISOString(),
                  invitation_expires_at: result.results[0].expiresAt
                } 
              : req
          )
        );
        
        toast({
          title: "Invitation Sent",
          description: `Account setup invitation sent to ${client.full_name} (${client.email})`,
        });
      } else {
        const error = result.results[0]?.error || 'Unknown error';
        toast({
          title: "Failed to Send Invitation",
          description: `Could not send invitation to ${client.full_name}: ${error}`,
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error('Error sending invitation:', error);
      toast({
        title: "Error",
        description: `Failed to send invitation: ${error.message || 'Unknown error'}`,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSendBulkInvitations = async (clients: any[]) => {
    console.log('Sending bulk invitations to clients:', clients);
    setIsProcessing(true);
    
    try {
      const clientIds = clients.map(client => client.id);
      const result: InvitationResponse = await sendBulkAccountSetupInvitations(clientIds);
      
      if (result.success && result.summary.successful > 0) {
        // Update local state for successful invitations
        const successfulResults = result.results.filter(r => r.success);
        const successfulIds = successfulResults.map(r => r.clientId);
        
        setOnboardingRequests((prevRequests) => 
          prevRequests.map(req => {
            const successResult = successfulResults.find(r => r.clientId === req.id);
            return successResult 
              ? { 
                  ...req, 
                  invitation_sent_at: new Date().toISOString(),
                  invitation_expires_at: successResult.expiresAt
                } 
              : req;
          })
        );
        
        toast({
          title: "Bulk Invitations Sent",
          description: `Successfully sent ${result.summary.successful} invitations out of ${result.summary.total} clients`,
        });

        if (result.summary.failed > 0) {
          // Show details of failed invitations
          const failedClients = result.results.filter(r => !r.success);
          console.warn('Failed invitations:', failedClients);
        }
      } else {
        toast({
          title: "Failed to Send Invitations",
          description: `Could not send any invitations. Please try again.`,
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error('Error sending bulk invitations:', error);
      toast({
        title: "Error",
        description: `Failed to send invitations: ${error.message || 'Unknown error'}`,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    isProcessing,
    handleSendInvitation,
    handleSendBulkInvitations
  };
};
